import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Flex, LoadingOverlay, Title } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import {
    CampaignInquiryDraftIntent,
    PartialCreateCampaignInquiryInput,
} from "src/components/CreateCampaignInquiryForm/utils";
import { CreateCampaignInquiryInput } from "src/graphql/generated/graphql";
import { useNavigation } from "src/hooks/useNavigation";
import { RoutePath } from "src/routes";

import { CreateCampaignInquiry } from "../../../components/CreateCampaignInquiry";
import { useAuthContext } from "../../../hooks/auth";
import { useCreateCampaignInquiry } from "../../../hooks/campaignInquiries";
import { handleError } from "../../../utils/error";

interface CreateCampaignInquiryLayoutProps {
    initialValues?: PartialCreateCampaignInquiryInput;
    isLoading?: boolean;
    /**
     * Identifies the piece of work being authored, so an unfinished draft is only
     * ever offered back on the same one (TTV2-1569). Defaults to a fresh inquiry.
     */
    draftIntent?: CampaignInquiryDraftIntent;
}

export const CreateCampaignInquiryLayout = ({
    initialValues,
    isLoading,
    draftIntent,
}: CreateCampaignInquiryLayoutProps) => {
    const { navigateTo } = useNavigation();
    const { t } = useTranslation(["common"]);

    const { client } = useAuthContext();

    const { createCampaignInquiry, isCreatingCampaignInquiry } = useCreateCampaignInquiry();

    const handleSave = useCallback(
        async (campaignInquiry: CreateCampaignInquiryInput) => {
            try {
                const createdCampaignInquiry = await createCampaignInquiry(campaignInquiry);

                if (createdCampaignInquiry?.id) {
                    navigateTo(RoutePath.CAMPAIGN_INQUIRY_DETAIL, {
                        campaignInquiryId: createdCampaignInquiry.id,
                    });
                    return true;
                }
                return false;
            } catch (error) {
                handleError(error, {
                    message: { key: "campaigninquiry_saving_failed", namespace: "common" },
                });
                return false;
            }
        },
        [createCampaignInquiry, navigateTo]
    );

    return (
        <>
            <LoadingOverlay visible={isCreatingCampaignInquiry || isLoading} />
            <Flex
                align="center"
                justify="space-between"
                mb="lg"
                pb="md"
                rowGap={8}
                wrap="wrap"
                style={{
                    borderBottom: "1px solid #E0E0E0",
                }}
            >
                <Flex gap={16} align="center">
                    <IconChevronLeft
                        stroke={2}
                        size={24}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigateTo(RoutePath.CAMPAIGNS_INQUIRIES)}
                    />
                    <Title order={3} style={{ fontWeight: 600 }}>
                        {t("campaigninquiry_create")}
                    </Title>
                </Flex>
            </Flex>
            <CreateCampaignInquiry
                key={client?.id}
                client={client}
                onCreate={handleSave}
                initialValues={initialValues}
                draftIntent={draftIntent}
            />
        </>
    );
};
